import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Globe, Github, Linkedin, MapPin, MessageSquare, Save } from "lucide-react";
import { Header } from "@/components/Header";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const OPEN_TO = ["Hiring", "Looking for work", "Mentoring", "Cofounder", "Speaking", "Advising"];
const field =
  "w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm outline-none transition-colors duration-200 focus:border-neutral-900";

const Avatar = ({ person, size = "h-20 w-20" }) =>
  person?.picture ? (
    <img src={person.picture} alt={person.name} className={`${size} shrink-0 rounded-full object-cover`} />
  ) : (
    <span className={`${size} grid shrink-0 place-items-center rounded-full bg-neutral-900 text-xl font-bold text-white`}>
      {(person?.name || "?")[0].toUpperCase()}
    </span>
  );

export default function Profile() {
  const { id } = useParams();
  const { user } = useAuth();
  const mine = !id || id === user?.user_id;
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const path = mine ? "/me/profile" : `/members/${id}`;
    apiFetch(path)
      .then((p) => {
        setProfile(p);
        setForm(p);
      })
      .catch(() => setProfile(false));
  }, [id, mine]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        headline: form.headline || "",
        working_on: form.working_on || "",
        company: form.company || "",
        skills: String(form.skills || "").toString().split(",").map((s) => s.trim()).filter(Boolean),
        neighborhood: form.neighborhood || "",
        open_to: form.open_to || [],
        site_url: form.site_url || "",
        linkedin_url: form.linkedin_url || "",
        github_url: form.github_url || "",
        notify_replies: !!form.notify_replies,
        notify_dms: !!form.notify_dms,
      };
      const updated = await apiFetch("/me/profile", { method: "PUT", body: JSON.stringify(body) });
      setProfile(updated);
      setForm(updated);
      toast.success("Profile saved");
    } catch (err) {
      toast.error(err.message);
    }
    setSaving(false);
  };

  if (profile === false) return <div className="p-10">Member not found.</div>;
  if (!profile) return <div className="min-h-screen bg-white" />;

  const skillsValue = Array.isArray(form.skills) ? form.skills.join(", ") : form.skills || "";

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-start gap-5">
          <Avatar person={profile} />
          <div className="min-w-0">
            <h1 className="text-3xl font-extrabold tracking-tight text-neutral-900" data-testid="profile-name">
              {profile.name}
            </h1>
            <p className="mt-1 text-neutral-600">{profile.headline || (mine ? "Add a headline below" : "Member")}</p>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 text-sm text-neutral-500">
              {profile.company && <span className="font-semibold text-neutral-700">{profile.company}</span>}
              {profile.neighborhood && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" /> {profile.neighborhood}
                </span>
              )}
              <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-neutral-600">
                {profile.role}
              </span>
            </p>
          </div>
        </div>

        {profile.working_on && (
          <div className="mt-8 rounded-2xl border border-neutral-200 p-5">
            <p className="text-xs font-bold uppercase tracking-wide text-neutral-400">Working on</p>
            <p className="mt-2 whitespace-pre-line text-neutral-700" data-testid="profile-working-on">
              {profile.working_on}
            </p>
          </div>
        )}

        {profile.skills?.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2" data-testid="profile-skills">
            {profile.skills.map((s) => (
              <span key={s} className="rounded-full bg-neutral-100 px-3 py-1 text-xs font-semibold text-neutral-700">
                {s}
              </span>
            ))}
          </div>
        )}

        {profile.open_to?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="profile-open-to">
            {profile.open_to.map((o) => (
              <span key={o} className="rounded-full border border-[#84CC16] px-3 py-1 text-xs font-bold text-[#4D7C0F]">
                Open to {o.toLowerCase()}
              </span>
            ))}
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          {profile.site_url && (
            <a href={profile.site_url} target="_blank" rel="noreferrer" data-testid="profile-site" className="flex items-center gap-1.5 rounded-full border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-50">
              <Globe className="h-4 w-4" /> Website
            </a>
          )}
          {profile.linkedin_url && (
            <a href={profile.linkedin_url} target="_blank" rel="noreferrer" data-testid="profile-linkedin" className="flex items-center gap-1.5 rounded-full border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-50">
              <Linkedin className="h-4 w-4" /> LinkedIn
            </a>
          )}
          {profile.github_url && (
            <a href={profile.github_url} target="_blank" rel="noreferrer" data-testid="profile-github" className="flex items-center gap-1.5 rounded-full border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-50">
              <Github className="h-4 w-4" /> GitHub
            </a>
          )}
          {!mine && (
            <Link to={`/messages?to=${profile.user_id}`} data-testid="profile-message-link" className="flex items-center gap-1.5 rounded-full bg-[#B8FF2E] px-4 py-2 text-sm font-bold text-[#14210A] transition-colors duration-200 hover:bg-[#A2E80B]">
              <MessageSquare className="h-4 w-4" /> Message
            </Link>
          )}
        </div>

        {!mine && !profile.headline && !profile.working_on && !profile.skills?.length && (
          <p className="mt-10 rounded-2xl border border-dashed border-neutral-300 p-10 text-center text-sm text-neutral-500" data-testid="profile-empty">
            {profile.name} hasn't filled out a profile yet — send them a message to say hello.
          </p>
        )}

        {mine && (
          <form onSubmit={save} className="mt-12 border-t border-neutral-200 pt-10" data-testid="profile-form">
            <h2 className="text-2xl font-bold tracking-tight text-neutral-900">Edit your profile</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <input data-testid="profile-headline-input" className={`${field} sm:col-span-2`} placeholder="Headline — e.g. Backend engineer, ex-Puppet" value={form.headline || ""} onChange={(e) => setForm({ ...form, headline: e.target.value })} />
              <textarea data-testid="profile-working-input" rows={3} className={`${field} sm:col-span-2`} placeholder="What are you working on right now?" value={form.working_on || ""} onChange={(e) => setForm({ ...form, working_on: e.target.value })} />
              <input data-testid="profile-company-input" className={field} placeholder="Company" value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} />
              <input data-testid="profile-neighborhood-input" className={field} placeholder="Neighborhood" value={form.neighborhood || ""} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} />
              <input data-testid="profile-skills-input" className={`${field} sm:col-span-2`} placeholder="Skills, comma separated" value={skillsValue} onChange={(e) => setForm({ ...form, skills: e.target.value })} />
              <input data-testid="profile-site-input" className={field} placeholder="https://yoursite.com" value={form.site_url || ""} onChange={(e) => setForm({ ...form, site_url: e.target.value })} />
              <input data-testid="profile-linkedin-input" className={field} placeholder="LinkedIn URL" value={form.linkedin_url || ""} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} />
              <input data-testid="profile-github-input" className={field} placeholder="GitHub URL" value={form.github_url || ""} onChange={(e) => setForm({ ...form, github_url: e.target.value })} />
            </div>

            <p className="mt-6 text-sm font-bold text-neutral-900">Open to</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {OPEN_TO.map((o) => {
                const on = (form.open_to || []).includes(o);
                return (
                  <button
                    key={o}
                    type="button"
                    data-testid={`open-to-${o}`}
                    onClick={() =>
                      setForm({
                        ...form,
                        open_to: on ? form.open_to.filter((x) => x !== o) : [...(form.open_to || []), o],
                      })
                    }
                    className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                      on ? "border-[#84CC16] bg-[#B8FF2E] text-[#14210A]" : "border-neutral-300 text-neutral-700 hover:bg-neutral-50"
                    }`}
                  >
                    {o}
                  </button>
                );
              })}
            </div>

            <p className="mt-8 text-sm font-bold text-neutral-900">Email notifications</p>
            <label className="mt-3 flex items-center gap-2.5 text-sm text-neutral-700">
              <input type="checkbox" data-testid="notify-replies" checked={!!form.notify_replies} onChange={(e) => setForm({ ...form, notify_replies: e.target.checked })} className="h-4 w-4 accent-[#65A30D]" />
              Email me when someone replies to my forum post
            </label>
            <label className="mt-2 flex items-center gap-2.5 text-sm text-neutral-700">
              <input type="checkbox" data-testid="notify-dms" checked={!!form.notify_dms} onChange={(e) => setForm({ ...form, notify_dms: e.target.checked })} className="h-4 w-4 accent-[#65A30D]" />
              Email me when a member sends me a direct message
            </label>

            <button
              disabled={saving}
              data-testid="profile-save"
              className="mt-8 flex items-center gap-2 rounded-full bg-[#B8FF2E] px-6 py-3 text-sm font-bold text-[#14210A] transition-colors duration-200 hover:bg-[#A2E80B] disabled:opacity-60"
            >
              <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save profile"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
